import { useDate } from '@bechtel/client-common';
import { Button, ButtonAppearance, Icon, IconSize, theme } from '@bechtel/gds';
import 'ag-grid-community/styles/ag-grid.css'; // Core grid CSS, always needed
import 'ag-grid-community/styles/ag-theme-alpine.css'; // Optional theme CSS
import { ColDef, ColGroupDef } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { cloneDeep } from 'lodash';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GridWrapperStyled } from './styled';
import {
  ClientGridProps,
  DEFAULT_FLOATING_FILTER_HEIGHT,
  DEFAULT_FONT_SIZE,
  DEFAULT_HEADER_HEIGHT,
  DEFAULT_ROW_HEIGHT,
} from './types';
export const ClientGrid = <T extends object & { originalValue?: T }>({
  colDefs,
  rowData,
  rowHeight,
  headerHeight,
  floatingFilterHeight,
  fontSize,
  onClick,
  loading,
  handleScroll,
  onSortChanged,
  initialSort,
}: ClientGridProps<T> & { children?: ReactNode }) => {
  const { formatISODate, formatTime } = useDate();
  const gridRef = useRef<AgGridReact<T>>(null);
  const [gridRows, setGridRows] = useState<T[]>();
  const defaultColumnDefs = useMemo<ColDef>(() => {
    return {
      sortable: false, // Disable client-side sorting
      resizable: true,
      suppressColumnsToolPanel: true,
    };
  }, []);

  const columnDefs: (ColDef<T> | ColGroupDef<T>)[] = useMemo(() => {
    const customColumnDefs: ColDef[] = colDefs.map((colDef: ColDef<T> | ColGroupDef<T>) => {
      return {
        filter: 'agTextColumnFilter',
        floatingFilter: true,

        valueFormatter: (params) => {
          if (params.colDef.field === 'createdAt' || params.colDef.field === 'nextScheduleAt') {
            if (params.value) {
              return `${formatISODate(params.value)} ${formatTime(params.value)}`;
            }
            return '';
          }
          if (params.colDef.field === 'identifier.0') {
            return params.value ? params.value.toString() : '';
          }
          if (params.colDef.field === 'entityTypeId') {
            return params.value ? params.value.toString() : '';
          }
          if (params.colDef.field === 'status') {
            return params.value ? params.value.toString() : '';
          }
          if (params.colDef.field === 'errorTypeId' || params.colDef.field === 'notificationTypeId') {
            return params.value ? params.value.toString() : '';
          }
          if (params.colDef.field === 'error') {
            return params.value ? JSON.stringify(params.value) : '';
          }

          return params.value;
        },
        flex: 1,
        width: colDef.headerName?.length,
        suppressHeaderContextMenu: true,
        cellStyle: {
          fontSize: fontSize ? fontSize : DEFAULT_FONT_SIZE,
          lineHeight: '1.5',
          fontFamily: theme.fontFamilies.compact,
          color: theme.colors.grey[600],
          alignItems: 'center',
        },
        ...colDef,
      };
    });

    return customColumnDefs;
  }, [colDefs, rowHeight, onClick]);

  useEffect(() => {
    if (!rowData || rowData.length === 0) return;
    const clonedRowData: T[] = cloneDeep(rowData); //Cloning the incoming data, as ag-grid will try to mutate it while editing.
    setGridRows([...clonedRowData]);
  }, [rowData]);

  const onSync = useCallback(
    (rowData: any) => {
      onClick && onClick(rowData);
    },
    [onClick]
  );

  return (
    <GridWrapperStyled fontSize={fontSize ?? DEFAULT_FONT_SIZE}>
      <AgGridReact<T>
        loading={loading}
        columnDefs={columnDefs}
        rowData={gridRows}
        defaultColDef={defaultColumnDefs}
        animateRows={true}
        ref={gridRef}
        suppressScrollOnNewData={true}
        tooltipShowDelay={500}
        onGridReady={(params) => {
          params.api.sizeColumnsToFit();
          params.api.autoSizeAllColumns();

          // Set initial sort if provided
          if (initialSort && initialSort.colId) {
            params.api.applyColumnState({
              state: [{ colId: initialSort.colId, sort: initialSort.sort }],
              defaultState: { sort: null }
            });
          }
        }}
        stopEditingWhenCellsLoseFocus={true}
        rowHeight={rowHeight ?? DEFAULT_ROW_HEIGHT}
        headerHeight={headerHeight ?? DEFAULT_HEADER_HEIGHT}
        floatingFiltersHeight={floatingFilterHeight ?? DEFAULT_FLOATING_FILTER_HEIGHT}
        onBodyScroll={() => {
          const api = gridRef.current?.api;
          if (!api) return;
          const bottom_px = api.getVerticalPixelRange().bottom;
          const grid_height = api.getDisplayedRowCount() * api.getSizesForCurrentTheme().rowHeight;
          // If the bottom of the grid is reached, trigger the scroll handler
          // This is a workaround to trigger the scroll handler when the grid is scrolled to the bottom
          // as ag-grid does not trigger the scroll event when the grid is scrolled to the bottom
          // This is needed to load more data when the user scrolls to the bottom
          if (bottom_px + 1 >= grid_height) {
            handleScroll && handleScroll();
          }
        }}
        onSortChanged={onSortChanged}
        suppressMultiSort={true} // Ensure single column sorting only
        components={{
          SyncButtonComponent: (params: any) => {
            return (
              <Button
                onClick={() => onSync(params.data)}
                disabled={loading}
                appearance={ButtonAppearance.TERTIARY}
                style={{ paddingBottom: theme.spacings[400] }}
              >
                <Icon size={IconSize.MEDIUM} icon="rotate" fillColor="bechtel.blue.800" />
              </Button>
            );
          },
        }}
      ></AgGridReact>
    </GridWrapperStyled>
  );
};
